import cx from 'classnames'

import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from './AutopilotDetailsV2.module.scss'
import { Button } from 'blaze-ui/Button'
import ClockBackward from 'blaze-ui/icons/20/ClockBackward'
import { Text } from 'blaze-ui/Text/Text'
import {
  CurrentPlan,
  PlanDetails,
  nameMap,
} from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { useRootStore } from 'stores/RootStore'
import { formatPrice, calculateMonthlyExtraAccountCharges } from '../../utils'
import { pluralize } from 'utils'
import dayjs from 'dayjs'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { Modal, useModals } from 'almanac-ui/Modal'
import { AlertTriangle, ArrowRefresh2, ArrowRight, CopyRight2 } from 'blaze-ui/icons'
import { useStripePaymentMethodUpdate } from 'hooks/useStripePaymentMethodUpdate'
import { useUpdateAutopilotSubscription } from 'hooks/useUpdateAutopilotSubscription'
import { getHref, getOrigin } from 'utils/location'

import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { findMonthlyPrice, findCreditLimit } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/Plans'
import { noop } from 'lodash'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useReferralUrl } from 'components/Settings/ReferralModalV2/hooks/useReferralUrl'
import { Email } from 'blaze-ui/icons/24'
import { ReferralModalV2 } from 'components/Settings/ReferralModalV2/ReferralModalV2'
import { IconButtonLink } from 'blaze-ui/IconButton/IconButtonLink'
import { FacebookBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { TwitterX } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/FeatureList'
import { observer } from 'mobx-react-lite'
import { PurchaseContentCreditsModal } from './components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import { InvoicesModal } from './components/InvoicesModal/InvoicesModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { generatePath, useHistory } from 'react-router-dom'
import { useOpenSettings } from 'hooks/useOpenSettings'
import { SettingScreen } from 'components/Settings/types'
import { useDispatch } from 'react-redux'
import * as Actions from 'redux/actions'
import { useWorkspaces } from 'hooks/useWorkspaces'
import client from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { WhiteLabelCheckoutModal } from 'components/Settings/Workspace/WhiteLabelSettings/WhiteLabelCheckoutModal'
import { AddOnCard } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/UpgradesAndAddOns/UpgradesAndAddOns'
import { CreditsCard } from './CreditsCard/CreditsCard'
import { PlanCard } from './PlanCard/PlanCard'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Tooltip } from 'components/Tooltip/Tooltip'
import Server02 from 'blaze-ui/icons/16/Server02'
import { useVirtualMarketerPlan } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/hooks/useVirtualMarketerPlan'
import { AutoReloadModal } from './components/AutoReloadModal/AutoReloadModal'
import { AUTOPILOT_INTEGRATIONS_PATH } from 'paths'

export const AutoReloadButton = ({
  onPress,
  className,
}: {
  onPress: (e: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
}) => {
  const workspace = useCurrentWorkspace()

  return (
    <button
      onClick={onPress}
      className={cx(Styles.autoReloadButton, { [Styles.active]: workspace?.auto_reload_credits }, className)}
    >
      {workspace?.auto_reload_credits ? 'ON' : 'OFF'}
    </button>
  )
}

export const AutopilotDetailsV2 = observer(() => {
  const isSettingsModalUpdateEnabled = useFlipperFlag('settings_modal_update')
  const billingUpgradesAndAddOnsEnabled = useFlipperFlag('billing_upgrades_and_add_ons')
  const creditsAndPlanCardEnabled = useFlipperFlag('billing_credits_and_plan_card')
  const { isMobile } = useResponsiveDesign()
  const { openModal, closeModal } = useModals()
  const dispatch = useDispatch()
  const workspaces = useWorkspaces()
  const history = useHistory()
  const { openSettings } = useOpenSettings()
  const workspace = useCurrentWorkspace()
  const { currentSubscription, autopilotPlans, autopilotAddOns } = useRootStore().tierUsageStore
  const { data: virtualMarketerPlan } = useVirtualMarketerPlan()
  const { updateSubscription, isUpdating } = useUpdateAutopilotSubscription()
  const autopilotContentCreditsAddon = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]
  const {
    creditUsage,
    videoCreditsUsed,
    adsCreditsUsed,
    contentCreditsUsed,
    creditsRemaining,
    usageCosts,
    startDate,
    endDate,
  } = useCreditUsage()
  const successUrl = `${getOrigin()}/checkout-session?session_id={CHECKOUT_SESSION_ID}&redirect_to_billing=true`
  const { isProcessing, redirectToStripe } = useStripePaymentMethodUpdate(successUrl, getHref())
  const { referralUrl } = useReferralUrl()
  const encodedReferralUrl = encodeURIComponent(referralUrl)
  const twitterPrefillText = encodeURIComponent(
    `Hey, I’ve been using @blazeaiapp to save 10+ hours a week, and it’s been a game-changer. Try it for free with my link: ${referralUrl}.`,
  )
  const refillDate = dayjs(endDate).add(1, 'day')

  if (!currentSubscription?.prices) return null

  const currentSubscriptionPlanPrice =
    autopilotPlans.find((plan) => plan.key === currentSubscription.plan)?.prices[0] || currentSubscription.prices[0]
  const price = currentSubscription.trial_ended ? currentSubscriptionPlanPrice : currentSubscription.prices[0]
  const intervalCount = currentSubscription.prices[0].recurring.interval_count

  const calculateDiscountedPrice = (unitAmount: number) => {
    const discount = currentSubscription?.discount
    if (!discount?.coupon) return unitAmount
    if (discount.coupon.percent_off) {
      return unitAmount * (1 - discount.coupon.percent_off / 100)
    }
    if (discount.coupon.amount_off) {
      return Math.max(0, unitAmount - discount.coupon.amount_off)
    }
    return unitAmount
  }

  const currentPlan: CurrentPlan = !workspace?.is_ap_freemium
    ? {
        key: currentSubscription?.plan,
        length: currentSubscription?.prices[0].recurring.interval_count,
        monthlyPrice: formatPrice(
          calculateDiscountedPrice(price.unit_amount) / intervalCount / 100 +
            calculateMonthlyExtraAccountCharges(currentSubscription),
        ),
      }
    : {
        key: PaymentPlanKey.Free,
        length: 1,
        monthlyPrice: 0,
      }
  const starterPlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotStarter)
  const growthPlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotGrowth)
  const enterprisePlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotEnterprise)
  const isOnEnterprisePlan = currentPlan.key === PaymentPlanKey.BlazeAutopilotEnterprise
  const isOnPioneer = currentPlan.key === PaymentPlanKey.VirtualMarketer
  const isTrialing = currentSubscription.status === 'trialing'
  const isCanceled = currentSubscription.status === 'canceled'
  const isTrialEnded = isCanceled && !!currentSubscription?.trial_ended
  const isPartiallyCanceled = !!currentSubscription?.cancel_at && currentSubscription.status === 'active'
  const isTrialingOrCanceled = isTrialing || isCanceled || isPartiallyCanceled
  const isOnStarterPlan = currentPlan.key === PaymentPlanKey.BlazeAutopilotStarter

  if (!price) return null

  const formattedNextPaymentDate = dayjs(currentSubscription.next_payment?.due_date).format('MMM D, YYYY')
  let planScheduleText: string
  if (currentSubscription.cancel_at) {
    planScheduleText = `Your ${nameMap[currentSubscription.plan]} plan ends on ${dayjs(
      currentSubscription.cancel_at,
    ).format('MMM D')} at ${dayjs(currentSubscription.cancel_at).format('h A')}.`
  } else if (isTrialing) {
    planScheduleText = `Your trial ends in ${currentSubscription.trial_days_left} days`
  } else {
    planScheduleText = `Next payment on ${formattedNextPaymentDate}`
  }

  const openPurchaseContentCreditsModal = () => {
    if (!autopilotContentCreditsPrice) return

    if (currentSubscription?.status === 'active') {
      openModal(PurchaseContentCreditsModal, { price: autopilotContentCreditsPrice }, { newStack: true })
    } else {
      openModal(AutopilotPlansModal, {}, { newStack: true })
    }
  }

  const openInvoicesModal = () => {
    openModal(InvoicesModal)
  }

  const showUpgradesAndAddOns = billingUpgradesAndAddOnsEnabled && !isTrialingOrCanceled && !isOnStarterPlan

  let choosePlanSectionLabel: string
  if (showUpgradesAndAddOns) {
    choosePlanSectionLabel = 'Upgrades and add-ons'
  } else if (currentSubscription?.status === 'canceled') {
    choosePlanSectionLabel = 'Choose plan'
  } else {
    choosePlanSectionLabel = 'Change plan'
  }

  const handleAddWhiteLabel = () => {
    openModal(WhiteLabelCheckoutModal, {
      onConfirm: async (priceId: string, paymentMethodId?: string) => {
        if (!workspace) return
        try {
          const response = await WorkspaceClient.purchaseWhiteLabel(workspace.id, priceId, paymentMethodId)
          if (response.type === SUCCESS_RESPONSE_TYPE) {
            await client.updateWorkspaceSettings({ workspace: { color_scheme: 'light' } }, workspace.key)
            dispatch(
              Actions.workspacesSet(
                workspaces.map((w) =>
                  w.key === workspace.key ? { ...w, is_whitelabel: true, color_scheme: 'light' } : w,
                ),
              ),
            )
            showToastMessage('White label added to your plan!', 'success')
            closeModal()
            openSettings(SettingScreen.General)
          } else {
            showToastMessage('There was an error purchasing white label', 'error')
          }
        } catch {
          showToastMessage('There was an error enabling white label', 'error')
        }
      },
      onCancel: closeModal,
    })
  }

  const handleAddExtraAccounts = () => {
    history.push(generatePath(AUTOPILOT_INTEGRATIONS_PATH, { workspaceId: workspace?.id }))
  }

  return (
    <div
      data-upgrades-and-add-ons={showUpgradesAndAddOns || isTrialingOrCanceled ? 'on' : 'off'}
      className={Styles.container}
    >
      {isSettingsModalUpdateEnabled && !isMobile ? (
        <Modal.Header
          className={Styles.header}
          title="Billing & usage"
          actions={
            <Button frontIcon={ClockBackward} variant="tertiary" onPress={openInvoicesModal}>
              Billing History
            </Button>
          }
        />
      ) : (
        <div className={Styles.header}>
          <Heading level={3}>Billing & usage</Heading>
          <Button frontIcon={ClockBackward} variant="tertiary" onPress={openInvoicesModal}>
            Billing History
          </Button>
        </div>
      )}

      {!isCanceled && !creditsAndPlanCardEnabled && (
        <div
          className={cx(Styles.currentPlanAndUsage, {
            [Styles.autoReload]: currentSubscription?.status === 'active',
          })}
        >
          <div className={cx(Styles.currentPlan, { [Styles.fullWidth]: isOnEnterprisePlan })}>
            <Text variant="label">Current plan</Text>
            <div className={cx(Styles.planDetails, { [Styles.fullWidth]: isOnEnterprisePlan })}>
              <div className={Styles.top}>
                <Heading level={4}>
                  {workspace?.is_ap_freemium ? 'Free' : nameMap[currentSubscription.plan]}{' '}
                  {isTrialing || isTrialEnded ? 'Trial' : 'Plan'}
                </Heading>
                <div className={Styles.planInfo}>
                  <div className={Styles.priceAndLength}>
                    <Text variant="smallList">${currentPlan.monthlyPrice}/mo</Text>
                    <Text variant="secondary" color="var(--dark-80)">
                      {price.recurring.interval_count} {pluralize('month', price.recurring.interval_count)}
                    </Text>
                  </div>
                  <Text variant="metadata" color="var(--dark-60)">
                    {planScheduleText}
                  </Text>
                  {!currentSubscription.cancel_at && isOnPioneer && (
                    <Text variant="metadata" color="var(--dark-60)">
                      This plan won't be available again if you cancel or switch.
                    </Text>
                  )}
                </div>
              </div>
              <div className={cx(Styles.bottom, { [Styles.fullWidth]: isOnEnterprisePlan })}>
                <Button variant="tertiary" isDisabled={isProcessing || isUpdating} onPress={redirectToStripe}>
                  Change Payment
                </Button>
                <Button
                  data-testid="renew-plan-button"
                  variant={currentSubscription.cancel_at ? 'primary' : 'secondary'}
                  isDisabled={isUpdating}
                  onPress={() => {
                    let plan = null

                    switch (currentSubscription.plan) {
                      case PaymentPlanKey.BlazeAutopilotGrowth:
                        plan = growthPlan
                        break
                      case PaymentPlanKey.BlazeAutopilotStarter:
                        plan = starterPlan
                        break
                      case PaymentPlanKey.VirtualMarketer:
                        plan = virtualMarketerPlan
                        break
                    }

                    if (!currentSubscription.cancel_at || !plan) {
                      openModal(AutopilotPlansModal, {}, { newStack: true })
                      return
                    }

                    if (plan.key === PaymentPlanKey.VirtualMarketer) {
                      updateSubscription({
                        paymentMethodId: undefined,
                        priceId: currentSubscription.prices?.[0]?.id,
                        reactivate: !!currentSubscription.cancel_at,
                      })
                      return
                    }

                    openModal(
                      AutopilotPlansModal,
                      {
                        initialStep: 'selected',
                        initialPlanAndPrice: {
                          plan: plan,
                          price: price,
                        },
                        reactivate: !!currentSubscription.cancel_at,
                      },
                      { newStack: true },
                    )
                  }}
                >
                  {currentSubscription.cancel_at ? 'Reactivate' : 'Manage Plan'}
                </Button>
              </div>
            </div>
          </div>
          {!isOnEnterprisePlan && (
            <div className={Styles.usage}>
              <Text variant="label">Usage</Text>
              {workspace?.is_ap_freemium ? (
                <div className={Styles.freeUsageDetails}>
                  <Heading level={4} color="var(--Magenta-70, #7F24B7)">
                    Your weekly posting limit
                  </Heading>
                  <Text variant="secondary" color="var(--dark-80)">
                    You have <strong>3</strong> posts per channel. Unlock more weekly posts — invite your friends!
                  </Text>
                  <div className={Styles.referralsContainer}>
                    <div className={Styles.referralUrl}>
                      <div className={Styles.referralInput}>
                        <Text lineClamp={1} color="var(--dark-40)">
                          {referralUrl}
                        </Text>
                      </div>
                      <IconButton variant="editor" icon={CopyRight2} />
                    </div>
                    <div className={Styles.referralButtons}>
                      <div className={Styles.leftButtons}>
                        <Button
                          frontIcon={Email}
                          variant="tertiary"
                          isDisabled={!referralUrl}
                          onPress={() => openModal(ReferralModalV2)}
                        >
                          Email
                        </Button>
                        <IconButtonLink
                          to={`https://www.facebook.com/sharer/sharer.php?u=${encodedReferralUrl}`}
                          target="_blank"
                          variant="tertiary"
                          icon={() => <FacebookBrand size={20} />}
                          isDisabled={!referralUrl}
                        />
                        <IconButtonLink
                          to={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedReferralUrl}`}
                          target="_blank"
                          variant="tertiary"
                          icon={() => <LinkedInBrand size={20} />}
                          isDisabled={!referralUrl}
                        />
                        <IconButtonLink
                          to={`https://twitter.com/intent/tweet?text=${twitterPrefillText}`}
                          target="_blank"
                          variant="tertiary"
                          icon={TwitterX}
                          isDisabled={!referralUrl}
                        />
                      </div>
                      <Button variant="editor" endIcon={ArrowRight} onPress={() => openModal(ReferralModalV2)}>
                        Open Referrals
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={Styles.usageDetails}>
                  <div className={Styles.usageHeader}>
                    <div className={Styles.titleAndUsage}>
                      <Heading level={4}>{creditsRemaining.toLocaleString()} credits remaining</Heading>
                    </div>
                    {currentSubscription?.status === 'active' && (
                      <div className={Styles.autoReloadNotificationContainer}>
                        <div className={Styles.iconAndText}>
                          <ArrowRefresh2 />
                          <Text variant="secondary">Auto-reload</Text>
                        </div>
                        <AutoReloadButton onPress={() => openModal(AutoReloadModal)} />
                      </div>
                    )}
                  </div>
                  {(creditUsage?.credits_expiring_soon || 0) > 0 && (
                    <div className={Styles.creditsExpiringSoon}>
                      <AlertTriangle color="var(--dark-60)" size={16} />
                      <Text variant="metadata" color="var(--dark-60)">
                        {creditUsage?.credits_expiring_soon} credits expiring in 7 days
                      </Text>
                    </div>
                  )}
                  {!currentSubscription.cancel_at && !isOnPioneer && (
                    <Text variant="metadata" color="var(--dark-60)">
                      {creditUsage?.initial_credits_available} credits reset on {refillDate.format('MMM D, YYYY')}
                    </Text>
                  )}
                  <div
                    className={cx(Styles.usageFooter, {
                      [Styles.autoReload]: currentSubscription?.status === 'active',
                    })}
                  >
                    <div className={Styles.usageBreakdown}>
                      <div className={Styles.usageBreakdownHeader}>
                        <Text variant="metadata" color="var(--dark-60)">
                          Credit usage from {startDate && dayjs(startDate).format('MMM D')} to{' '}
                          {endDate && dayjs(endDate).format('MMM D')}
                        </Text>
                      </div>
                      <div className={Styles.usageBreakdownContent}>
                        {!isOnPioneer && (
                          <Tooltip
                            placement="top"
                            label={
                              <div className={Styles.tooltip}>
                                <span>
                                  Blaze generates your ad
                                  <br />
                                  creative: videos, images, <br />
                                  and copy. {usageCosts?.ads_credits_cost} credits per ad.
                                </span>
                              </div>
                            }
                            hide={false}
                          >
                            <div className={Styles.breakdown}>
                              <Text variant="metadata" color="var(--dark-80)">
                                AI managed ads ({usageCosts?.ads_credits_cost} each):
                              </Text>
                              <div className={Styles.serverIconAndText}>
                                <Server02 size={12} />
                                <Text variant="metadata" color="var(--dark-80)">
                                  {adsCreditsUsed}
                                </Text>
                              </div>
                            </div>
                          </Tooltip>
                        )}
                        <Tooltip
                          placement="top"
                          label={
                            <div className={Styles.tooltip}>
                              <span>
                                Blaze generates custom video <br />
                                from scratch. {usageCosts?.video_credits_cost || 0} credits per video.
                              </span>
                            </div>
                          }
                        >
                          <div className={Styles.breakdown}>
                            <Text variant="metadata" color="var(--dark-80)">
                              AI generated video ({usageCosts?.video_credits_cost || 0} each):
                            </Text>
                            <div className={Styles.serverIconAndText}>
                              <Server02 size={12} />
                              <Text variant="metadata" color="var(--dark-80)">
                                {videoCreditsUsed}
                              </Text>
                            </div>
                          </div>
                        </Tooltip>
                        <Tooltip
                          placement="top"
                          label={
                            <div className={Styles.tooltip}>
                              <span>
                                Blazes generates an AI image in your <br />
                                brand's style. 3 credits per image.
                              </span>
                            </div>
                          }
                        >
                          <div className={Styles.breakdown}>
                            <Text variant="metadata" color="var(--dark-80)">
                              Stylized image ({usageCosts?.visual_styles_credits_cost || 0} each):
                            </Text>
                            <div className={Styles.serverIconAndText}>
                              <Server02 size={12} />
                              <Text variant="metadata" color="var(--dark-80)">
                                {creditUsage?.visual_styles_credits_used || 0}
                              </Text>
                            </div>
                          </div>
                        </Tooltip>
                        {!isOnPioneer && (
                          <Tooltip
                            placement="top"
                            label={
                              <div className={cx(Styles.tooltip, Styles.content)}>
                                <span>
                                  Blogs, social posts, and <br />
                                  emails. {usageCosts?.content_credits_cost || 0} credit per piece. AI-
                                  <br />
                                  generated videos cost extra.
                                </span>
                              </div>
                            }
                          >
                            <div className={Styles.breakdown}>
                              <Text variant="metadata" color="var(--dark-80)">
                                All other content ({usageCosts?.content_credits_cost || 0} each):
                              </Text>
                              <div className={Styles.serverIconAndText}>
                                <Server02 size={12} />
                                <Text variant="metadata" color="var(--dark-80)">
                                  {contentCreditsUsed}
                                </Text>
                              </div>
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    {currentSubscription?.status === 'active' ? (
                      <div className={cx(Styles.usageCtaButtonContainer, Styles.autoReload)}>
                        <span className={Styles.refer}>
                          <span className={Styles.underline} onClick={() => openModal(ReferralModalV2)}>
                            Refer a friend
                          </span>{' '}
                          to get more credits
                        </span>
                        <div className={Styles.ctaButtons}>
                          <Button
                            variant="secondary"
                            onPress={() => {
                              if (currentSubscription.status === 'trialing') {
                                openModal(AutopilotPlansModal, {}, { newStack: true })
                              } else {
                                openModal(AutoReloadModal)
                              }
                            }}
                          >
                            {workspace?.auto_reload_credits ? 'Manage Auto-Reload' : 'Enable Auto-Reload'}
                          </Button>
                          <Button onPress={openPurchaseContentCreditsModal} isDisabled={!autopilotContentCreditsPrice}>
                            Buy More Credits
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className={Styles.usageCtaButtonContainer}>
                        <Button variant="tertiary" onPress={() => openModal(ReferralModalV2)}>
                          {isOnPioneer ? 'Earn Free Credits' : 'Refer and Earn'}
                        </Button>
                        <Button
                          variant="secondary"
                          onPress={openPurchaseContentCreditsModal}
                          isDisabled={!autopilotContentCreditsPrice}
                        >
                          {isOnPioneer ? 'Add More Credits' : 'Buy More Credits'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {creditsAndPlanCardEnabled && (
        <div className={Styles.billingCardsRow}>
          <PlanCard />
          <CreditsCard />
        </div>
      )}
      {!isOnEnterprisePlan && (
        <div className={Styles.getMoreCreditsAndUpgrade}>
          <div
            data-upgrades-and-add-ons={showUpgradesAndAddOns || isTrialingOrCanceled ? 'on' : 'off'}
            className={Styles.upgradePlan}
          >
            <Text variant="label" color={showUpgradesAndAddOns ? 'var(--dark-40)' : 'var(--dark-80)'}>
              {choosePlanSectionLabel}
            </Text>
            <div className={Styles.plans}>
              {isTrialingOrCanceled && !!starterPlan && (
                <div className={Styles.starterPlanWrapper}>
                  <PlanDetails
                    planKey={starterPlan.key}
                    monthlyPrice={findMonthlyPrice(starterPlan)}
                    creditLimit={findCreditLimit(starterPlan)}
                    currentPlan={currentPlan}
                    onSelectPlan={() =>
                      openModal(
                        AutopilotPlansModal,
                        {
                          initialStep: 'selected',
                          initialPlanAndPrice: {
                            plan: starterPlan,
                            price: starterPlan.prices[0],
                          },
                          reactivate: !!currentSubscription.cancel_at,
                        },
                        { newStack: true },
                      )
                    }
                    isTrialing={isTrialing}
                    isProcessing={false}
                  />
                </div>
              )}
              {!isTrialingOrCanceled && showUpgradesAndAddOns ? (
                <>
                  <div className={Styles.planWrapper}>
                    <AddOnCard
                      type="white_label"
                      onAddToPlan={handleAddWhiteLabel}
                      isActive={!!workspace?.is_whitelabel}
                    />
                  </div>
                  <div className={Styles.planWrapper}>
                    <AddOnCard
                      type="extra_accounts"
                      onAddToPlan={handleAddExtraAccounts}
                      isActive={currentSubscription?.subscription_items?.some(
                        (item) => item.plan_key === PaymentPlanKey.BlazeAutopilotExtraAccounts,
                      )}
                    />
                  </div>
                </>
              ) : (
                growthPlan && (
                  <div className={Styles.planWrapper}>
                    <PlanDetails
                      planKey={growthPlan.key}
                      monthlyPrice={findMonthlyPrice(growthPlan)}
                      creditLimit={findCreditLimit(growthPlan)}
                      currentPlan={currentPlan}
                      onSelectPlan={() =>
                        openModal(
                          AutopilotPlansModal,
                          {
                            initialStep: 'selected',
                            initialPlanAndPrice: {
                              plan: growthPlan,
                              price: growthPlan.prices[0],
                            },
                            reactivate: !!currentSubscription.cancel_at,
                          },
                          { newStack: true },
                        )
                      }
                      isTrialing={isTrialing}
                      isProcessing={false}
                    />
                  </div>
                )
              )}
              {enterprisePlan && (
                <div className={Styles.planWrapper}>
                  <PlanDetails
                    planKey={enterprisePlan.key}
                    monthlyPrice={findMonthlyPrice(enterprisePlan)}
                    creditLimit={findCreditLimit(enterprisePlan)}
                    currentPlan={currentPlan}
                    onSelectPlan={noop}
                    isTrialing={isTrialing}
                    isProcessing={false}
                    billingSettingsVariant={showUpgradesAndAddOns}
                    flexHeight={isTrialingOrCanceled && billingUpgradesAndAddOnsEnabled}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className={Styles.questionsContainer}>
        <Heading level={5}>Questions</Heading>
        <Text variant="secondary">
          Email <a href="mailto:support@blaze.ai">support@blaze.ai</a> or click the question mark icon in the bottom
          right to chat with us.
        </Text>
      </div>
    </div>
  )
})
